/**
 * Asking the browser whether a new release exists.
 *
 * Registering a worker is not enough. The browser only re-fetches sw.js on a
 * navigation within scope (and on its own ~24h schedule), so a window left
 * open — which is precisely what an INSTALLED app is for — would never learn
 * that a release had shipped. The update prompt would then almost only appear
 * to people who were reloading anyway, which is the one group that does not
 * need it.
 *
 * So the page asks, on a timer and whenever it comes back to the foreground.
 */

/** 15 minutes. Long enough not to chatter, short enough that a release
 *  reaches an open window within a coffee break. */
export const CHECK_INTERVAL_MS = 15 * 60 * 1000;

/** Nothing is retried faster than this, however many events arrive — tab
 *  switching can fire visibilitychange many times a minute. */
export const MIN_GAP_MS = 60 * 1000;

export const shouldCheck = (now: number, lastCheck: number | null, minGapMs = MIN_GAP_MS): boolean =>
  lastCheck === null || now - lastCheck >= minGapMs;

export interface CheckPorts {
  update: () => Promise<unknown>;
  now: () => number;
  /** Returns a teardown, so a test can stop the timer. */
  setInterval: (fn: () => void, ms: number) => () => void;
  onVisible: (fn: () => void) => () => void;
  isVisible: () => boolean;
}

/**
 * Wires both triggers to one throttled check. Returns a teardown.
 */
export const startUpdateChecks = (p: CheckPorts): (() => void) => {
  let last: number | null = null;

  const check = () => {
    const now = p.now();
    if (!shouldCheck(now, last)) return;
    last = now;
    // Offline, or a worker that has gone away: there is nothing to tell the
    // person, and an unhandled rejection here would be noise in their console.
    void p.update().catch(() => { /* nothing to do */ });
  };

  const stopTimer = p.setInterval(() => { if (p.isVisible()) check(); }, CHECK_INTERVAL_MS);
  const stopVisible = p.onVisible(check);
  return () => { stopTimer(); stopVisible(); };
};
